/**
 * replay.ts — game recording (Phase 1 of tasks/game-replay-plan.md).
 *
 * A game is fully reconstructable from its INPUTS: {level/pack, simVersion, gameSpeed, RNG seed,
 * roster, ordered command stream}. The sim is deterministic (MP = lockstep), so re-simulating those
 * inputs reproduces the whole game exactly (Phase 3). We record inputs, not state — compact and exact.
 *
 * ReplayRecorder captures one lockstep MP game into a self-describing `.wirep` container. Capture is
 * verbatim: each block's command batch is the same raw 28-byte Message blobs the DO already relays
 * (already byte-parity with the C++ command struct), so a re-sim is bit-identical.
 *
 * `.wirep` layout (big-endian via ByteWriter; JSON header/footer are UTF-8, length-prefixed):
 *   "WIREP"            5 bytes magic
 *   formatVer          u8
 *   headerLen          u32   header JSON (metadata + seed + roster)
 *   header             headerLen bytes (UTF-8)
 *   blockCount         u32
 *   per block:         u32 block, u32 syncHash (v2), u16 cmdCount, cmdCount × MESSAGE_SIZE cmds, cmdCount × u8 side (v3)
 *   footerLen          u32   footer JSON (result + duration)
 *   footer             footerLen bytes (UTF-8)
 */
import { ByteWriter, ByteReader } from '../../../protocol/bytebuffer.ts';
import { MESSAGE_SIZE } from '../../../protocol/gamemsg.ts';

export const WIREP_MAGIC = 'WIREP';
export const WIREP_FORMAT_VER = 3; // v2 +agreed per-block syncHash; v3 +issuing side per command (older still parse)

export interface ReplayRosterEntry {
  pid: number;
  side: number;
  color: string; // game.cpp ColorFromSide (neutral/blue/red/yellow/cyan)
  name: string;
  flags: number; // kfPlr* (COMPUTER = bot, CREATOR, etc.)
}

export interface ReplayHeader {
  formatVer: number;
  simVersion: number; // GameParams.dwVersionSimulation — re-sim REQUIRES a matching build
  packId: number;
  packHash: string; // hex
  level: string; // szLvlFilename
  title: string;
  gameSpeed: number; // tGameSpeed (cmsUpdate = tGameSpeed*10)
  seed: number; // ScBeginGame ulRandomSeed (>>> 0) — do NOT normalize away; re-sim needs the exact seed
  roster: ReplayRosterEntry[];
  startedAt: number; // unix seconds
}

export interface ReplayFooter {
  player_stats: unknown[]; // per-player winstats (WINNER/LOSER flags, counts) — the outcome
  durationBlocks: number; // last lockstep block reached
  endedAt: number; // unix seconds
  // Desync marker (forensics flush only — game.ts buildDesyncBundle). ABSENT on the normal
  // completed-game path, so those .wirep bytes are unchanged (the footer is length-prefixed
  // JSON; an absent optional key adds zero bytes). Parsers of older files never see it.
  desync?: {
    block: number | null; // the divergent block (null = AUR-overflow trip, no comparison)
    lastStraw: { pid: number; cUpdatesBlock: number; hash: number }; // the UR that tripped it
  };
  // /bots/game runner outcome (server-paced all-bot games only) — the GROUND-TRUTH end reason, written
  // by botgame.ts via Game.setBotsOutcome. The command stream alone can't show it: a decide-by-resign
  // game never relays the deciding resign (the runner breaks first) and bot games set no WINNER flag, so
  // without this the analyzer can't tell a decided game from a capped one. ABSENT on human games and
  // pre-this-version recordings (an absent optional key adds zero footer bytes; older parsers ignore it).
  botsOutcome?: {
    outcome: 'decided' | 'cap' | 'syncError' | 'killed' | 'error';
    winner: number | null; // the winning SIDE on a decisive end (null otherwise)
    maxBlocks: number; // the game-update cap in force → lets a reader name cpuCap vs blockCap exactly
  };
}

export interface ReplayBlock {
  block: number;
  cmds: Uint8Array[]; // raw 28-byte Message blobs, verbatim
  syncHash?: number; // v2: agreed per-block sim checksum all clients reported (0/undefined = unrecorded —
  // solo, or the divergent block of a desync). Lets a re-sim self-check vs the real run.
  sides?: number[]; // v3: issuing side per command (parallel to cmds; 0 = unknown/server). The DO knows the
  // sender of each batch → per-player attribution with no re-sim. undefined for v1/v2.
}

export interface ParsedReplay {
  header: ReplayHeader;
  blocks: ReplayBlock[];
  footer: ReplayFooter;
}

const te = new TextEncoder();
const td = new TextDecoder();

function writeJson(w: ByteWriter, obj: unknown): void {
  const bytes = te.encode(JSON.stringify(obj));
  w.writeDword(bytes.length);
  w.writeBytes(bytes);
}
function readJson<T>(r: ByteReader): T {
  const len = r.readDword();
  return JSON.parse(td.decode(r.readBytes(len))) as T;
}

export function serializeReplay(
  header: ReplayHeader,
  blocks: ReplayBlock[],
  footer: ReplayFooter,
): Uint8Array {
  const w = new ByteWriter();
  w.writeBytes(te.encode(WIREP_MAGIC));
  w.writeByte(WIREP_FORMAT_VER); // the byte = the layout this serializer writes (always current)
  writeJson(w, header);
  w.writeDword(blocks.length);
  for (const b of blocks) {
    w.writeDword(b.block);
    w.writeDword((b.syncHash ?? 0) >>> 0); // v2: agreed per-block sim checksum (0 = unrecorded)
    w.writeWord(b.cmds.length);
    for (const c of b.cmds) {
      if (c.length !== MESSAGE_SIZE)
        throw new RangeError(`replay: command size ${c.length} != ${MESSAGE_SIZE}`);
      w.writeBytes(c);
    }
    for (let i = 0; i < b.cmds.length; i++) w.writeByte((b.sides?.[i] ?? 0) & 0xff); // v3: issuing side per command
  }
  writeJson(w, footer);
  return w.toUint8Array();
}

export function parseReplay(buf: Uint8Array): ParsedReplay {
  const r = new ByteReader(buf);
  const magic = td.decode(r.readBytes(WIREP_MAGIC.length));
  if (magic !== WIREP_MAGIC) throw new Error(`not a .wirep (magic=${JSON.stringify(magic)})`);
  const formatVer = r.readByte();
  if (formatVer < 1 || formatVer > WIREP_FORMAT_VER)
    throw new Error(`unsupported .wirep formatVer ${formatVer}`);
  const header = readJson<ReplayHeader>(r);
  const blockCount = r.readDword();
  const blocks: ReplayBlock[] = [];
  for (let i = 0; i < blockCount; i++) {
    const block = r.readDword();
    const syncHash = formatVer >= 2 ? r.readDword() : 0; // v2+ carries the agreed per-block checksum
    const cmdCount = r.readWord();
    const cmds: Uint8Array[] = [];
    for (let j = 0; j < cmdCount; j++) cmds.push(r.readBytes(MESSAGE_SIZE));
    const sides = formatVer >= 3 ? Array.from(r.readBytes(cmdCount)) : undefined; // v3+: issuing side per command
    blocks.push({ block, cmds, syncHash, sides });
  }
  const footer = readJson<ReplayFooter>(r);
  return { header, blocks, footer };
}

/** Replay storage key: `<yyyy-mm-dd>/<gameId>.wirep` under the replays bucket. */
export function replayKey(startUtc: number, gameId: number, nowMs: number): string {
  const secs = startUtc || Math.floor(nowMs / 1000);
  const ymd = new Date(secs * 1000).toISOString().slice(0, 10);
  return `${ymd}/${gameId}.wirep`;
}

/** Buffers one game's inputs in memory (KB–few MB), then serializes the `.wirep` at game end. With an
 *  optional store-backed block source the big per-block command log lives off-heap (the DO's SQLite via
 *  GameStore) — appendBlock already persisted the identical row, so block() skips the heap push and
 *  blocksAfter/serialize read from the source. The tiny syncHashes Map stays in heap either way. */
export class ReplayRecorder {
  private readonly header: ReplayHeader;
  private readonly blocks: ReplayBlock[] = []; // empty when store-backed: the source supplies the blocks
  private storedCount = 0; // block() count when store-backed (the source has no cheap length); else unused
  private readonly syncHashes = new Map<number, number>(); // block → agreed sim checksum (arrives via CsUpdateResult, after the block was emitted)
  // When present, the durable source of the per-block command log (the store rows for this game). Returns
  // ReplayBlock[] WITHOUT syncHash (the recorder merges its own syncHashes Map); ascending by block.
  private readonly source?: () => ReplayBlock[];

  /** `loadBlocks` (optional) makes the recorder store-backed: the heap block log is dropped and the blocks
   *  are read from the store on demand (blocksAfter/serialize). The Game wires it to read its store rows. */
  constructor(header: Omit<ReplayHeader, 'formatVer'>, loadBlocks?: () => ReplayBlock[]) {
    this.header = { ...header, formatVer: WIREP_FORMAT_VER };
    this.source = loadBlocks;
  }

  /** Record one emitted lockstep update frame — called once per relayed block, EMPTY and non-empty
   *  alike. The client runs exactly one sim step per received update (SimUI.cpp: one gsim.Update per
   *  ProcessUpdateMessage), so an empty GAMEUPDATEEMPTY tick is still a sim step; a faithful re-sim
   *  must replay the same frames in the same order. Empty blocks cost ~6 bytes (block# + zero count).
   *  Store-backed: the row is already durable (the Game's appendBlock), so we only bump the count and
   *  let the source supply the bytes. Heap mode: snapshot the arrays; the command blobs are already
   *  private copies from the decoder, so the shallow array copy is safe — this runs on the 250ms tick. */
  block(block: number, cmds: Uint8Array[], sides: number[] = []): void {
    if (this.source) {
      this.storedCount++;
      return;
    }
    this.blocks.push({ block, cmds: cmds.slice(), sides: sides.slice() });
  }

  /** Store-backed RESTORE: seed the emitted-block count from the persisted rows (which already hold the
   *  command log off-heap) instead of replaying them — block() doesn't buffer when store-backed anyway. */
  seedCount(n: number): void {
    if (this.source) this.storedCount = n;
  }

  /** Record the agreed per-block sim checksum (from checkSyncError, once all clients ack a block). It
   *  arrives AFTER the block was emitted, so it's merged onto the block (by number) at serialize. */
  recordSyncHash(block: number, hash: number): void {
    this.syncHashes.set(block, hash >>> 0);
  }

  get blockCount(): number {
    return this.source ? this.storedCount : this.blocks.length;
  }

  /** The per-block command log: the store-backed source (off-heap) when present, else the in-heap
   *  buffer. The single place blocksAfter + serialize ask "where do the blocks come from". */
  private allBlocks(): ReplayBlock[] {
    return this.source ? this.source() : this.blocks;
  }

  /** Merge the late-arriving agreed syncHash onto each block (by number). The store source carries no
   *  syncHash; the heap blocks may already have 0. */
  private withSyncHashes(blocks: ReplayBlock[]): ReplayBlock[] {
    return this.syncHashes.size
      ? blocks.map((b) => ({ ...b, syncHash: this.syncHashes.get(b.block) ?? 0 }))
      : blocks;
  }

  /** The recorded blocks emitted AFTER `block`, in order — the relay replays these to a reconnecting
   *  client (game.ts tryResume) to fast-forward its sim to the live edge. */
  blocksAfter(block: number): ReplayBlock[] {
    return this.withSyncHashes(this.allBlocks().filter((b) => b.block > block));
  }

  serialize(footer: ReplayFooter): Uint8Array {
    return serializeReplay(this.header, this.withSyncHashes(this.allBlocks()), footer);
  }
}
